// Pick'em: who wins each of the league's games this week (scenario I6,
// PLAN-2026-10-18-families-and-newcomers §9.1, B-447; the second cut,
// PLAN-2026-10-23-pickem-that-explains-itself §3, B-484).
//
// IT SAYS WHAT IT IS. The first cut was a grid of team buttons with nothing
// saying what a tap did, when it stopped counting or what it was for. Now the
// head says the game in a line, with the rules behind an info button; the
// week's card says where my picks stand in words, and while open how many
// are in; each game is two sides facing each other across a "vs", the picked
// one filled and saying so; and the rules are a panel of their own.
//
// ONE QUESTION, A CARD A GAME: the two teams as buttons, saved on the tap. The
// week locks at its first kickoff; until then a team sees only its own picks
// and how many teams have picked, and after it each side says who picked it,
// and a final game marks the winner and whether my pick was right. The
// season's table sits beside the games on a desktop and under them
// elsewhere, a bot's row saying how it picks. A side carries its record and,
// at Standard, its projected points from the week's scoreboard, as the
// matchup shows them; never a win chance beside a pick, which would read as
// odds (the no-gambling decision). No player, no real-world line: the
// league's own games, for pride (DIRECTION A16).
//
// A REAL LEAGUE'S ALONE: the practice league's simulator plays no pick'em
// yet, and the screen says so rather than drawing an empty week.
//
// AND BULLSEYE (PLAN-2026-10-30-bullseye): under the picks while the week is
// open, my team's guess at its own score, a box and Save; after the lock every
// guess beside its team, nearest first once final, the week's Bullseye marked.
// The season's table counts bullseyes.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GUESS_MAX,
  pickemPoints,
  valueOf,
  type PickemMatchupShape,
  type PickemViewShape,
  type Snapshot,
  type TeamRef,
} from '@fsp/domain';
import { useAtLeast } from '../data/detail';
import { useReaderZone, deviceZone, useScoreboard } from '../data/hooks';
import { saveGuess, savePicks, usePickem } from '../data/pickem';
import { TeamMark } from '../kit';
import { Icon } from '../kit/Icon';
import { Panel } from '../kit/Panel';
import { Term } from '../kit/Why';
import { Button, Chip, Line, Meter, Stack, Text, TextField, Title, tell } from '../kit/ui';
import { useTier } from '../shell/useTier';
import { FromLeague } from './shared/FromLeague';
import { PickemInfo, PickemSteps, lockToName } from './shared/PickemHow';
import { fmtWhen } from './shared/RosterMoveSheet';
import { WeekStepper } from './shared/WeekStepper';
import '../styles/screens/pickem.css';

/** "6", "6.5": a half point is the only fraction pick'em makes. */
function points(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** "4–3", "4–3–1": a team's record as the standings write it. */
function recordOf(t: TeamRef): string {
  const { wins, losses, ties } = t.record;
  return ties > 0 ? `${wins}–${losses}–${ties}` : `${wins}–${losses}`;
}

export function PickemScreen() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('period');
  const period = raw !== null && /^\d+$/.test(raw) ? Number(raw) : undefined;
  const { source, leagueId, snapshot, reload } = usePickem(period);
  // A SAVE'S ANSWER IS THE WEEK AS IT NOW STANDS, drawn at once; the read is
  // asked again, and whatever it answers next takes over — so the lock
  // passing, or another league opened on the same screen, is never hidden
  // behind an old save.
  const [saved, setSaved] = useState<PickemViewShape | null>(null);
  const fresh = valueOf(snapshot);
  useEffect(() => setSaved(null), [fresh, leagueId]);

  if (source === 'sim') {
    return (
      <div className="fsp-screen">
        <div className="fsp-page-scroll">
          <div className="fsp-pickem">
            <Panel title="Pick’em">
              {/* TODO(I6): pick'em in the practice league's simulator. */}
              <Text tone="secondary">
                Pick’em plays in real leagues: each week every team calls the winner of the league’s
                games, for pride. The practice league does not have it yet.
              </Text>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fsp-screen">
      <FromLeague snapshot={snapshot as Snapshot<PickemViewShape>}>
        {(fetched) => {
          const view =
            saved !== null &&
            saved.leagueId === fetched.leagueId &&
            saved.period.index === fetched.period.index
              ? saved
              : fetched;
          return (
            <PickemBody
              view={view}
              onWeek={(p) => {
                setSaved(null);
                setParams({ period: String(p) }, { replace: true });
              }}
              onPick={async (matchupId, teamId) => {
                if (leagueId === null) return;
                const out = await savePicks(leagueId, view.period.index, [{ matchupId, teamId }]);
                if (out.ok) {
                  setSaved(out.view);
                  reload();
                } else tell(out.message, 'bad');
              }}
              onGuess={async (guess) => {
                if (leagueId === null) return false;
                const out = await saveGuess(leagueId, view.period.index, guess);
                if (out.ok) {
                  setSaved(out.view);
                  reload();
                  // WHAT WAS KEPT, in tenths, not what was typed.
                  const kept = out.view.myGuess;
                  tell(kept === null ? 'Guess cleared.' : `Guess saved: ${tenths(kept)}.`, 'good');
                } else tell(out.message, 'bad');
                return out.ok;
              }}
            />
          );
        }}
      </FromLeague>
    </div>
  );
}

/** What the week's scoreboard says of a side: its projected points, or its score once final. */
type SideFigure = { projected: number | null; score: number };

function PickemBody({
  view,
  onWeek,
  onPick,
  onGuess,
}: {
  view: PickemViewShape;
  onWeek: (period: number) => void;
  onPick: (matchupId: string, teamId: string) => Promise<void>;
  /** Resolves true once saved. */
  onGuess: (guess: number | null) => Promise<boolean>;
}) {
  const zone = useReaderZone() ?? deviceZone();
  // Simple drops the counts, the records and the points, and keeps every sentence (DETAIL_LEVELS).
  const numbers = useAtLeast('standard');
  const tier = useTier();
  // A desktop lays the rules and the table beside the games; narrower stacks them.
  const wide = tier === 'desktop' || tier === 'large';
  const [busy, setBusy] = useState<string | null>(null);
  // THE SIDES' FIGURES ARE THE SCOREBOARD'S, for the same week: a projected
  // total as the matchup shows it, a final's score. Read beside the week, not
  // on the League page's card, which has no room for them.
  const board = valueOf(useScoreboard(view.period.index));
  const figures = new Map<string, SideFigure>();
  for (const m of board?.matchups ?? []) {
    figures.set(`${m.id}|${m.home.team.id}`, { projected: m.home.projected, score: m.home.score });
    figures.set(`${m.id}|${m.away.team.id}`, { projected: m.away.projected, score: m.away.score });
  }

  const pick = async (matchupId: string, teamId: string) => {
    if (busy !== null) return;
    setBusy(matchupId);
    await onPick(matchupId, teamId);
    setBusy(null);
  };

  const rules = (
    <Panel title="How pick’em works" bodyClass="fsp-pickem-body">
      <PickemSteps lock={lockToName(view)} zone={zone} />
    </Panel>
  );
  return (
    <div className="fsp-page-scroll">
      <div className="fsp-pickem" data-wide={wide || undefined}>
        <Stack gap={4}>
          <Stack gap={1}>
            <Line gap={1}>
              <Title>Pick’em</Title>
              <PickemInfo lock={lockToName(view)} zone={zone} />
            </Line>
            <Text tone="secondary" block>
              Call the winner of each game in your league every week. A right pick is a point.
            </Text>
          </Stack>
          {view.weeks.length > 0 ? (
            <WeekStepper
              periods={view.weeks.map((w) => w.index)}
              current={view.period.index}
              label={view.period.label}
              onGo={onWeek}
            />
          ) : null}

          <div className="fsp-pickem-grid">
            <div className="fsp-pickem-main">
              <Panel
                title={`${view.period.label}’s games`}
                meta={view.locked ? <Chip icon="locked">Locked</Chip> : undefined}
                bodyClass="fsp-pickem-body"
              >
                <Stack gap={3}>
                  <WeekState view={view} zone={zone} numbers={numbers} />
                  {view.matchups.length === 0 ? (
                    <Text tone="secondary" block>
                      Nothing to pick this week: no two teams play each other in it, or its games
                      are not drawn yet.
                    </Text>
                  ) : (
                    <ol className="fsp-pickem-games">
                      {view.matchups.map((m, i) => (
                        <GameCard
                          key={m.id}
                          index={i}
                          game={m}
                          canPick={view.canPick && busy === null}
                          saving={busy === m.id}
                          numbers={numbers}
                          figureOf={(teamId) => figures.get(`${m.id}|${teamId}`) ?? null}
                          onPick={(teamId) => void pick(m.id, teamId)}
                        />
                      ))}
                    </ol>
                  )}
                  <Bullseye view={view} numbers={numbers} onGuess={onGuess} />
                </Stack>
              </Panel>
            </div>
            <div className="fsp-pickem-aside">
              <Stack gap={4}>
                {wide ? rules : null}
                <SeasonTable view={view} numbers={numbers} />
                {wide ? null : rules}
              </Stack>
            </div>
          </div>
        </Stack>
      </div>
    </div>
  );
}

/**
 * WHERE THE WEEK STANDS, in words: what to do and until when while it is open,
 * that it is locked once it is, and how my week went once its games are over.
 * The count and its meter at Standard.
 */
function WeekState({
  view,
  zone,
  numbers,
}: {
  view: PickemViewShape;
  zone: string;
  numbers: boolean;
}) {
  const games = view.matchups.length;
  if (games === 0) return null;
  const mine = view.matchups.filter((m) => m.myPick !== null).length;
  const until = view.locksAt !== null ? fmtWhen(view.locksAt, zone) : null;
  if (!view.locked) {
    // HOW MANY TEAMS HAVE PICKED, never which, at Standard (B-447).
    const field = numbers ? (
      <Text size="sm" tone="secondary" block>
        {view.teamsPicked} {view.teamsPicked === 1 ? 'team has' : 'teams have'} picked so far.
      </Text>
    ) : null;
    if (!view.canPick)
      return (
        <Stack gap={1}>
          <Text size="sm" tone="secondary" block>
            Only the league’s teams pick. Everybody’s picks show here once the week locks
            {until !== null ? `, ${until}` : ', when it starts'}.
          </Text>
          {field}
        </Stack>
      );
    return (
      <div className="fsp-pickem-state">
        <Stack gap={2}>
          <Text block>
            {mine === games
              ? `All your picks are in. You can change them until ${until ?? 'the week starts'}.`
              : `Choose the team you think will win each game. Picks save as you go, and you can change them until ${until ?? 'the week starts'}.`}
          </Text>
          {numbers ? (
            <div className="fsp-pickem-progress">
              <Meter value={mine / games} label={`${mine} of ${games} picked`} />
              <Text size="sm" tone="secondary" nowrap>
                {mine} of {games} picked
              </Text>
            </div>
          ) : null}
          {field}
        </Stack>
      </div>
    );
  }
  const finals = view.matchups.filter((m) => m.result !== null);
  const done = finals.length === games;
  const myFinals = finals.filter((m) => m.myPick !== null);
  const myPoints = myFinals.reduce(
    (n, m) => n + pickemPoints(m.myPick!, { final: true, winnerTeamId: m.result!.winnerTeamId }),
    0,
  );
  const right = myFinals.filter((m) => m.result!.winnerTeamId === m.myPick).length;
  return (
    <div className="fsp-pickem-state">
      <Stack gap={1}>
        <Text block>
          {done
            ? 'This week’s games are over. Every team’s picks are shown.'
            : `Locked${until !== null ? ` ${until}` : ''}: every team’s picks are shown.`}
        </Text>
        {numbers && myFinals.length > 0 ? (
          <Text size="sm" tone="secondary" block>
            You called {right} of {myFinals.length}
            {done ? '' : ' so far'}: {points(myPoints)} {myPoints === 1 ? 'point' : 'points'}.
          </Text>
        ) : null}
      </Stack>
    </div>
  );
}

/** One game: its two sides facing each other, and after the lock who picked each. */
function GameCard({
  index,
  game: m,
  canPick,
  saving,
  numbers,
  figureOf,
  onPick,
}: {
  index: number;
  game: PickemMatchupShape;
  canPick: boolean;
  saving: boolean;
  numbers: boolean;
  figureOf: (teamId: string) => SideFigure | null;
  onPick: (teamId: string) => void;
}) {
  const mineIn = m.home.isMine || m.away.isMine;
  const tie = m.result !== null && m.result.winnerTeamId === null;
  return (
    <li
      className="fsp-pickem-game"
      aria-label={`Game ${index + 1}: ${m.home.name} against ${m.away.name}`}
    >
      {mineIn || m.kind !== 'regular' || tie ? (
        <Line gap={2} wrap>
          {mineIn ? <Chip tone="accent">Your game</Chip> : null}
          {m.kind !== 'regular' ? (
            <Chip>{m.kind === 'playoff' ? 'Playoffs' : 'Consolation'}</Chip>
          ) : null}
          {tie ? <Chip>A tie</Chip> : null}
        </Line>
      ) : null}
      <div
        className="fsp-pickem-sides"
        role="group"
        aria-label={`${m.home.name} or ${m.away.name}`}
      >
        {(['home', 'away'] as const).map((which, i) => {
          const t = m[which];
          const side = (
            <Side
              key={t.id}
              team={t}
              picked={m.myPick === t.id}
              won={m.result !== null && m.result.winnerTeamId === t.id}
              final={m.result !== null}
              tie={tie}
              disabled={!canPick}
              saving={saving}
              numbers={numbers}
              figure={figureOf(t.id)}
              pickers={m.pickedBy === null ? null : m.pickedBy[which]}
              onPick={() => onPick(t.id)}
            />
          );
          return i === 0
            ? [
                side,
                <span key="vs" className="fsp-pickem-vs" aria-hidden>
                  vs
                </span>,
              ]
            : side;
        })}
      </div>
    </li>
  );
}

function Side({
  team: t,
  picked,
  won,
  final,
  tie,
  disabled,
  saving,
  numbers,
  figure,
  pickers,
  onPick,
}: {
  team: TeamRef;
  picked: boolean;
  won: boolean;
  final: boolean;
  tie: boolean;
  disabled: boolean;
  saving: boolean;
  numbers: boolean;
  figure: SideFigure | null;
  /** Who picked this side, once the week has locked; null before. */
  pickers: readonly TeamRef[] | null;
  onPick: () => void;
}) {
  const facts = numbers
    ? [
        recordOf(t),
        figure === null
          ? null
          : final
            ? `${points(Math.round(figure.score * 10) / 10)} pts`
            : figure.projected !== null
              ? `${figure.projected.toFixed(1)} proj`
              : null,
      ].filter((x): x is string => x !== null)
    : [];
  // MY PICK ONCE THE GAME IS FINAL: right, a tie's half, or missed.
  const verdict = !picked || !final ? null : won ? 'right' : tie ? 'tie' : 'missed';
  return (
    <div className="fsp-pickem-side-col">
      <button
        type="button"
        className="fsp-pickem-side"
        aria-pressed={picked}
        data-won={won || undefined}
        data-saving={(saving && !picked) || undefined}
        disabled={disabled}
        onClick={onPick}
      >
        <TeamMark team={t} size={32} />
        <span className="fsp-pickem-who">
          <span className="fsp-pickem-name">{t.name}</span>
          {facts.length > 0 ? (
            <span className="fsp-pickem-facts fsp-num">{facts.join(' · ')}</span>
          ) : null}
        </span>
        {picked ? (
          <span className="fsp-pickem-mark" aria-hidden>
            <Icon name="accept" size="sm" />
          </span>
        ) : null}
      </button>
      <div className="fsp-pickem-under">
        {picked ? (
          verdict === null ? (
            <Text size="xs" tone="accent" weight="strong">
              Your pick
            </Text>
          ) : (
            <Chip tone={verdict === 'right' ? 'good' : verdict === 'tie' ? 'neutral' : 'bad'}>
              {verdict === 'right'
                ? 'Your pick · right'
                : verdict === 'tie'
                  ? 'Your pick · a tie'
                  : 'Your pick · missed'}
            </Chip>
          )
        ) : won ? (
          <Chip tone="good">Won</Chip>
        ) : null}
        {pickers === null ? null : pickers.length === 0 ? (
          <Text size="xs" tone="secondary" block>
            Nobody picked them.
          </Text>
        ) : (
          // WHO PICKED IT, AS THEIR MARKS: a list of names ran to five lines
          // a side once every team had picked (2026-10-23). The names are a
          // tap or a hover away, and what a screen reader hears.
          <Term name="Picked by" tip={pickers.map((t) => t.name).join(', ')}>
            <span className="fsp-pickem-pickers">
              <span className="fsp-pickem-pickers-label">Picked by</span>
              <span className="fsp-pickem-marks" aria-hidden>
                {pickers.map((t) => (
                  <TeamMark key={t.id} team={t} size={22} />
                ))}
              </span>
              {numbers ? <span className="fsp-num fsp-pickem-count">{pickers.length}</span> : null}
            </span>
          </Term>
        )}
      </div>
    </div>
  );
}

/** Tenths, as every score is shown: "112", "112.5". */
function tenths(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * BULLSEYE (PLAN-2026-10-30-bullseye §5): while the week is open, my team's
 * guess at its own score; once it locks, everybody's, nearest first.
 */
function Bullseye({
  view,
  numbers,
  onGuess,
}: {
  view: PickemViewShape;
  numbers: boolean;
  onGuess: (guess: number | null) => Promise<boolean>;
}) {
  const mine = view.matchups.some((m) => m.home.isMine || m.away.isMine);
  // WHAT I AM TYPING, or null while I am not, when the box shows the saved
  // guess: a read landing mid-word never overwrites it, and a save hands the
  // box back to the guess it answers with.
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (view.locked) {
    const guesses = view.guesses ?? [];
    if (guesses.length === 0) return null;
    return (
      <div className="fsp-bullseye" role="group" aria-label="Bullseye">
        <Text weight="strong" block>
          🎯 Bullseye
        </Text>
        <ol className="fsp-bullseye-list">
          {guesses.map((g) => (
            <li
              key={g.team.id}
              data-mine={g.team.isMine || undefined}
              data-won={g.bullseye || undefined}
            >
              <TeamMark team={g.team} size={28} />
              <span className="fsp-bullseye-who">
                <span className="fsp-bullseye-line">
                  <span className="fsp-bullseye-name">{g.team.name}</span>
                  {g.bullseye ? <span className="fsp-bullseye-mark">🎯 Bullseye</span> : null}
                </span>
                <span className="fsp-num fsp-bullseye-figures">
                  {`guessed ${tenths(g.guess)}`}
                  {g.scored !== null ? ` · scored ${g.scored.toFixed(1)}` : ''}
                  {numbers && g.miss !== null ? ` · off by ${g.miss.toFixed(1)}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (!view.canPick || !mine) return null;
  const shown = draft ?? (view.myGuess === null ? '' : tenths(view.myGuess));
  const parsed = shown.trim() === '' ? null : Number(shown);
  const bad = parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > GUESS_MAX);
  const save = async () => {
    if (bad || busy) return;
    setBusy(true);
    if (await onGuess(parsed)) setDraft(null);
    setBusy(false);
  };
  return (
    <div className="fsp-bullseye" role="group" aria-label="Bullseye">
      <Text weight="strong" block>
        🎯 Guess your score
      </Text>
      <Text size="sm" tone="secondary" block>
        The guess nearest its own team’s score wins the week’s Bullseye.
        {view.guessesIn > 0 && numbers
          ? ` ${view.guessesIn} ${view.guessesIn === 1 ? 'team has' : 'teams have'} guessed.`
          : ''}
      </Text>
      <form
        className="fsp-bullseye-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <TextField
          label="Your score this week"
          hideLabel
          grow
          inputMode="decimal"
          placeholder="Your score this week"
          value={shown}
          onChange={setDraft}
          maxLength={6}
          error={bad ? `A score from 0 to ${GUESS_MAX}.` : undefined}
        />
        <Button submit loading={busy} disabled={bad || parsed === view.myGuess}>
          {parsed === null && view.myGuess !== null ? 'Clear' : 'Save'}
        </Button>
      </form>
      <Text size="sm" tone="secondary" block>
        {view.myGuess !== null ? `Saved: ${tenths(view.myGuess)}.` : 'No guess yet.'}
        {numbers && view.myAverage !== null ? ` ${recentLine(view)}` : ''}
      </Text>
    </div>
  );
}

/** My team's recent scores, a fact to guess from, over the games it has: up to three. */
function recentLine(view: PickemViewShape): string {
  const n = view.myAverageGames;
  const average = tenths(view.myAverage ?? 0);
  return n <= 1
    ? `Your team scored ${average} in its last game.`
    : `Your team averaged ${average} over its last ${n === 2 ? 'two' : 'three'} games.`;
}

function SeasonTable({ view, numbers }: { view: PickemViewShape; numbers: boolean }) {
  const scored = view.table.some((r) => r.decided > 0);
  return (
    <Panel
      title="The season’s table"
      bodyClass="fsp-pickem-body"
      meta={
        view.table.length > 0 && numbers ? (
          <Text size="sm" tone="secondary">
            {view.table.length} playing
          </Text>
        ) : undefined
      }
    >
      {view.table.length === 0 ? (
        <Text tone="secondary" block>
          The table starts with the first games: a team is on it once a game it picked has begun, or
          once the week it guessed its score in is over.
        </Text>
      ) : (
        <Stack gap={2}>
          {scored ? null : (
            <Text size="sm" tone="secondary" block>
              No points yet: they come in as each week’s games finish.
            </Text>
          )}
          <ol className="fsp-pickem-table">
            {view.table.map((r, i) => (
              <li key={r.team.id} data-mine={r.team.isMine || undefined}>
                <span className="fsp-pickem-rank fsp-num">{i + 1}</span>
                <TeamMark team={r.team} size={28} />
                <span className="fsp-pickem-who">
                  <span className="fsp-pickem-name">{r.team.name}</span>
                  {r.way !== null ? <span className="fsp-pickem-way">{r.way}</span> : null}
                </span>
                {numbers ? (
                  <span className="fsp-pickem-score">
                    <span className="fsp-num fsp-pickem-points">
                      {points(r.points)} {r.points === 1 ? 'pt' : 'pts'}
                    </span>
                    {r.decided > 0 ? (
                      <span className="fsp-num fsp-pickem-right">
                        {r.correct} of {r.decided} right
                      </span>
                    ) : null}
                    {r.bullseyes > 0 ? (
                      <span className="fsp-num fsp-pickem-right">
                        🎯 {r.bullseyes} {r.bullseyes === 1 ? 'bullseye' : 'bullseyes'}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </Stack>
      )}
    </Panel>
  );
}
